import { Card, Col, Flex, Row, Table, Typography } from "antd";
import type { ColumnType } from "antd/lib/table";
import type { ApiPessoaTelefone } from "features/pessoa/types";
import type { ApiCalculo } from "pages/calculo/types/ApiCalculo";
import type { ApiCalculoColeta } from "pages/calculo/types/ApiCalculoColeta";
import type { ApiCalculoDesconto } from "pages/calculo/types/ApiCalculoDesconto";
import { decimalFormater, formatDocument, numberToCurrency, numberToDecimal } from "std/format";
import { styled } from "styled-components";

const { Text, Title } = Typography;

const TextTitle = styled(Text)`
  // flex: 0 0 100px;
  font-weight: bold;
  word-break: normal;
  // text-align: right;
`;

const TextInfo = styled(Text)`
  // flex: 1 1 calc(100% - 104px);
`;

const TitleSummary = styled(Text)`
  font-size: 16px;
  font-weight: bold !important;
  margin: 0px;
`;

const StyledCol = styled(Col)`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

export const getTipoConv = (value) => {
	if (value === 0) {
		return "Convênio";
	}

	if (value === 1) {
		return "Adiantamento";
	}

	if (value === 2) {
		return "Pagamento";
	}

	if (value === 3) {
		return "Funrural";
	}

	if (value === 4) {
		return "Fundesa";
	}

	if (value === 5) {
		return "Teste de Antibiótico";
	}

	if (value === 6) {
		return "Crioscopia";
	}

	if (value === 7) {
		return "Outros";
	}

	if (value === 8) {
		return "Descarte";
	}

	if (value === 9) {
		return "Senar";
	}

	return "Desconto";
};

export const columnsColeta: ColumnType<ApiCalculoColeta>[] = [
	{
		dataIndex: "calc_col_data",
		key: "calc_col_data",
		title: "Data",
	},
	{
		dataIndex: "calc_col_produto_nome",
		key: "calc_col_produto_nome",
		title: "Item",
	},
	{
		dataIndex: "calc_col_volume",
		key: "calc_col_volume",
		title: "Volume",
		align: "right",
		render: (value) => decimalFormater(value || 0),
	},
	{
		dataIndex: "calc_col_vlr_litro",
		key: "calc_col_vlr_litro",
		title: "Valor Litro",
		align: "right",
		render: (value) => numberToCurrency(value || 0, 3),
	},
	{
		dataIndex: "calc_col_vlr_total",
		key: "calc_col_vlr_total",
		title: "Total",
		align: "right",
		render: (value) => numberToCurrency(value || 0, 3),
	},
];

export const columnsDesconto: ColumnType<ApiCalculoDesconto>[] = [
	{
		dataIndex: "calc_desc_data",
		key: "calc_desc_data",
		title: "Data",
	},
	{
		dataIndex: "calc_desc_tipo",
		key: "calc_desc_tipo",
		title: "Item",
		render: (value) => getTipoConv(value || 0),
	},
	{
		title: "Fornecedor Conveniado",
		dataIndex: "convenio",
		key: "convenio",
		align: "center",
		render: (convenio) => <>{convenio?.fornecedorConveniado?.pessoa?.pes_nome_razaosocial || ""}</>,
	},
	{
		dataIndex: "calc_desc_volume",
		key: "calc_desc_volume",
		title: "Quantidade",
		align: "right",
		render: (value) => decimalFormater(value || 1),
	},
	{
		dataIndex: "calc_desc_valor",
		key: "calc_desc_valor",
		title: "Total",
		align: "right",
		render: (value) => numberToCurrency(value || 0, 3),
	},
];

export function TableCard({ calculo }: { calculo: ApiCalculo }) {
	const getDefaultTelefone = (telefones: ApiPessoaTelefone[]): string => {
		if (!telefones || telefones.length === 0) {
			return "";
		}

		const findTelDefault = telefones.find((tel) => tel.tel_principal === 1);

		if (findTelDefault) {
			return findTelDefault?.tel_telefone;
		}

		return telefones[0]?.tel_telefone || "";
	};

	return (
		<Card>
			<Flex vertical={true} gap={"12px"} style={{ width: "100%" }}>
				<div>
					<Flex gap={"12px"}>
						<StyledCol>
							<TextTitle>Produtor:</TextTitle>
							<TextInfo>
								{calculo?.produtor?.for_codigoindividual || ""} {calculo?.produtor?.pessoa?.pes_nome_razaosocial || ""}
							</TextInfo>
						</StyledCol>

						<StyledCol>
							<TextTitle>Transportador:</TextTitle>
							<TextInfo>
								{calculo?.produtor?.rota?.transportador?.tra_codigoindividual || ""}{" "}
								{calculo?.produtor?.rota?.transportador?.pessoa?.pes_nome_razaosocial || ""}
							</TextInfo>
							<TextInfo>{calculo?.produtor?.rota?.rot_identificacao || ""}</TextInfo>
						</StyledCol>

						<StyledCol>
							<TextTitle>Mês Ref:</TextTitle>
							<TextInfo>{calculo?.calc_mes || ""}</TextInfo>
						</StyledCol>
					</Flex>

					<Flex gap={"12px"}>
						<StyledCol>
							<TextTitle>CPF/CNPJ:</TextTitle>
							<TextInfo>{formatDocument(calculo?.produtor?.pessoa?.pes_cnpj_cpf || "")}</TextInfo>
						</StyledCol>

						<StyledCol>
							<TextTitle>Contato:</TextTitle>
							<TextInfo>{getDefaultTelefone(calculo?.produtor?.pessoa?.telefone || [])}</TextInfo>
						</StyledCol>
					</Flex>

					<Flex gap={"12px"}>
						<StyledCol>
							<TextTitle>Nome Fantasia:</TextTitle>
							<TextInfo>{calculo?.produtor?.pessoa?.pes_nome_fantasia || ""}</TextInfo>
						</StyledCol>

						<StyledCol>
							<TextTitle>Banco:</TextTitle>
							<TextInfo>{calculo?.produtor?.for_banco || ""}</TextInfo>
						</StyledCol>

						<StyledCol>
							<TextTitle>Agência:</TextTitle>
							<TextInfo>
								{calculo?.produtor?.for_agencia || ""}
								{calculo?.produtor?.for_agencia_dv ? `-${calculo?.produtor?.for_agencia_dv}` : ""}
							</TextInfo>
						</StyledCol>

						<StyledCol>
							<TextTitle>Conta:</TextTitle>
							<TextInfo>
								{calculo?.produtor?.for_conta || ""}
								{calculo?.produtor?.for_conta_dv ? `-${calculo?.produtor?.for_conta_dv}` : ""}
							</TextInfo>
						</StyledCol>
					</Flex>
				</div>

				<Flex vertical={true} gap={"12px"} style={{ width: "100%" }}>
					<div>
						<Title level={4} style={{ margin: "0px" }}>
							Créditos
						</Title>

						<Row style={{ width: "100%" }}>
							<Table
								style={{ width: "100%" }}
								columns={columnsColeta}
								dataSource={calculo?.coletas || []}
								pagination={false}
								size="small"
								rowClassName="zebra"
							/>
						</Row>
					</div>

					<div>
						<Title level={4} style={{ margin: "0px" }}>
							Descontos
						</Title>

						<Row style={{ width: "100%" }}>
							<Table
								style={{ width: "100%" }}
								columns={columnsDesconto}
								dataSource={calculo?.descontos || []}
								pagination={false}
								size="small"
								rowClassName="zebra"
							/>
						</Row>
					</div>

					<div>
						<Title level={4}>Resultado no mês</Title>

						<Flex justify="space-between" style={{ width: "100%" }}>
							<Flex vertical={true} gap={"4px"} style={{ width: "100%" }}>
								<StyledCol>
									<TitleSummary>NF/Série:</TitleSummary>
									<TitleSummary>
										{calculo?.calc_nfe_numero_doc || ""}/{calculo?.calc_nfe_serie_doc || ""}
									</TitleSummary>
								</StyledCol>

								<StyledCol xs={18}>
									<TitleSummary>Pagamento:</TitleSummary>
									<TitleSummary>{calculo?.calc_data_pagamento || ""}</TitleSummary>
								</StyledCol>
							</Flex>

							<Flex vertical={true} align="end" gap={"4px"} style={{ width: "100%" }}>
								<StyledCol>
									<TitleSummary>Créditos:</TitleSummary>
									<TitleSummary>{numberToCurrency(calculo?.calc_valor || 0, 3)}</TitleSummary>
								</StyledCol>

								<StyledCol>
									<TitleSummary>Descontos:</TitleSummary>
									<TitleSummary>{numberToCurrency(calculo?.calc_desconto_geral || 0, 3)}</TitleSummary>
								</StyledCol>

								<StyledCol>
									<TitleSummary>Valor Total:</TitleSummary>
									<TitleSummary>{numberToCurrency(calculo?.calc_valor_total || 0, 3)}</TitleSummary>
								</StyledCol>

								<StyledCol>
									<TitleSummary>Total de litragem:</TitleSummary>
									<TitleSummary>{numberToDecimal(calculo?.calc_volume || 0, 3)}</TitleSummary>
								</StyledCol>
							</Flex>
						</Flex>
					</div>
				</Flex>
			</Flex>
		</Card>
	);
}
